import { useState } from 'react'
import api, { Target } from '../../services/api'
import Button from '../common/Button'
import Input from '../common/Input'

interface AddTargetModalProps {
  organisationId: string
  onClose: () => void
  onSuccess: () => void
}

const AddTargetModal: React.FC<AddTargetModalProps> = ({ organisationId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<Target>({
    email: '',
    given_name: '',
    surname: '',
    job_title: '',
    department: ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validate()) return

    try {
      setSubmitting(true)
      setErrors({})
      
      await api.targetPool.addSingle(organisationId, formData)
      onSuccess()
    } catch (err: any) {
      setErrors({ submit: err.message || 'Failed to add target' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Add Target
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
            error={errors.email}
            required
            autoFocus
          />

          <Input
            label="Given Name"
            value={formData.given_name}
            onChange={(e) => setFormData({ ...formData, given_name: e.target.value })}
            placeholder="John"
            error={errors.given_name}
          />

          <Input
            label="Surname"
            value={formData.surname}
            onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
            placeholder="Doe"
            error={errors.surname}
          />

          <Input
            label="Job Title"
            value={formData.job_title}
            onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
            placeholder="Software Engineer"
            error={errors.job_title}
          />

          <Input
            label="Department"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            placeholder="Engineering"
            error={errors.department}
          />

          {errors.submit && (
            <div className="text-red-600 dark:text-red-400 text-sm">
              {errors.submit}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              className="flex-1"
            >
              {submitting ? 'Adding...' : 'Add Target'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddTargetModal
